import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ActionMeta, GroupBase, MultiValue, SingleValue } from 'chakra-react-select';
import { Combobox } from '../../../../core';
import { OptionBase } from '../../../../core/components/Combobox/types';
import { DROPDOWN_SEARCH_MINIMUM } from '../../../../constants';
import { i18n } from '../../../../i18n/translate';
import { patchDummyInputAriaDescribedBy } from '../SelectComponentWrappers';

export interface SelectControlOption extends OptionBase {
  [key: string]: string | number | boolean | null | undefined;
}

export interface SelectControlProps<T extends OptionBase = SelectControlOption> {
  'aria-describedby'?: string;
  caption?: string;
  clearable?: boolean;
  clearValue?: boolean;
  dataValue?: Partial<T> | T['value'] | T | null;
  defaultValue?: Partial<T> | T['value'] | T | null;
  disabled?: boolean;
  forcePlaceholder?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  isFloatOutside?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  joinValues?: boolean;
  labelKey?: keyof T | string;
  displayProperty?: keyof T | string;
  menuPosition?: 'fixed' | 'absolute';
  menuPlacement?: 'auto' | 'top' | 'bottom';
  noOptionsMessage?: ((inputValue: string) => string) | string;
  multi?: boolean;
  name?: string;
  onBlur?: (value: T['value'] | T['value'][] | null) => void;
  onChange?: (value: T | T[] | null) => void;
  onValueChange?: (obj: Record<string, T['value'] | T['value'][] | null>) => void;
  options?: readonly T[];
  placeholder?: string | null;
  placeholderText?: string;
  required?: boolean;
  scrollContainerId?: string;
  searchable?: boolean;
  setError?: (error: boolean, message?: string) => void;
  sortBy?: string;
  valueKey?: keyof T | string;
  valueOverride?: boolean;
  valueProperty?: keyof T | string;
}

export interface SelectControlHandle {
  isValid: () => boolean;
  checkError: () => void;
}

interface RestProps {
  caption?: string;
  [key: string]: unknown;
}

function getPropValue(obj: object | null | undefined, key: string | number | symbol): unknown {
  if (obj == null) return undefined;
  return (obj as Record<string | number | symbol, unknown>)[key];
}

function isObject(val: unknown): val is object {
  return val != null && typeof val === 'object' && !Array.isArray(val);
}

const SelectControlInner = forwardRef(
  <T extends OptionBase = SelectControlOption>(
    {
      'aria-describedby': ariaDescribedBy,
      caption,
      clearable,
      clearValue,
      dataValue,
      defaultValue,
      disabled,
      forcePlaceholder,
      isDisabled,
      isFloatOutside: _isFloatOutside,
      isMulti,
      isSearchable,
      labelKey: labelKeyProp = 'label',
      displayProperty,
      menuPosition,
      multi,
      name,
      onBlur: onBlurProp,
      onChange: onChangeProp,
      onValueChange,
      options = [],
      placeholder,
      placeholderText,
      required,
      searchable,
      setError,
      valueKey,
      valueProperty,
      ...rest
    }: SelectControlProps<T>,
    ref: React.Ref<SelectControlHandle>
  ) => {
    const isMultiSelect = multi || isMulti || false;
    const vKey = valueKey ?? valueProperty ?? 'value';
    const labelKey = displayProperty ?? labelKeyProp;

    const propsRef = useRef<RestProps>(rest);
    propsRef.current = rest;

    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * For non-searchable selects, react-select renders its own internal DummyInput and
     * bypasses our custom InputWrapper. This patches aria-describedby on that DummyInput
     * so screen readers can still announce error/hint messages from FormField.
     */
    useEffect(() => {
      patchDummyInputAriaDescribedBy(containerRef.current, ariaDescribedBy);
    });

    const resolveValue = useCallback(
      (dv: T['value'] | T | null | undefined): T | T[] | null => {
        if (dv === undefined || dv === null) return isMultiSelect ? [] : null;
        if (!options?.length) return null;

        if (isMultiSelect) {
          const dvArr = Array.isArray(dv) ? dv : [dv];
          return options.filter(opt =>
            dvArr.some((d: T['value'] | T) => {
              const dVal = typeof d === 'object' && d !== null ? (getPropValue(d, String(vKey)) ?? d) : d;
              return String(getPropValue(opt, String(vKey))) === String(dVal);
            })
          ) as T[];
        }
        const dvObj = typeof dv === 'object' && dv !== null ? dv : null;
        const dvValue = dvObj ? (getPropValue(dvObj, String(vKey)) ?? dv) : dv;
        return (options.find(opt => String(getPropValue(opt, String(vKey))) === String(dvValue)) ?? null) as T | null;
      },
      [options, vKey, isMultiSelect]
    );

    const [value, setValue] = useState<T | T[] | null>(() =>
      resolveValue((dataValue ?? defaultValue) as T['value'] | T | null | undefined)
    );
    // Track latest value in a ref so blur/checkError always see the current selection,
    // avoiding stale-closure false-validation when blurInputOnSelect fires after handleChange.
    const valueRef = useRef<T | T[] | null>(null);
    valueRef.current = value;

    // Sync value when options load or dataValue changes (supports async-populated edit forms).
    // Guard: only sync once per unique dataValue to avoid overwriting user interaction,
    // matching legacy setValueOverride's hasSetDataValue pattern.
    const lastSyncedDataValue = useRef<Partial<T> | T['value'] | T | null>(null);
    useEffect(() => {
      if (
        options?.length &&
        dataValue !== undefined &&
        dataValue !== null &&
        dataValue !== lastSyncedDataValue.current
      ) {
        lastSyncedDataValue.current = dataValue ?? null;
        const resolved = resolveValue(dataValue as T['value'] | T);
        setValue(resolved);
        valueRef.current = resolved;
      }
    }, [options, dataValue, resolveValue]);

    useEffect(() => {
      if (clearValue) setValue(isMultiSelect ? [] : null);
    }, [clearValue, isMultiSelect]);

    const getOptionLabel = useCallback(
      // Match legacy getDefaultOptionLabel: also check attributes (JSON:API items)
      (opt: T) => {
        const attrs = getPropValue(opt, 'attributes');
        const attrsObj = isObject(attrs) ? attrs : null;
        return String(
          getPropValue(opt, labelKey) ?? getPropValue(attrsObj, labelKey) ?? getPropValue(opt, 'label') ?? ''
        );
      },
      [labelKey]
    );

    const getOptionValue = useCallback(
      // Match legacy getDefaultOptionValue: also check attributes, data, and id fallback
      (opt: T) => {
        const attrs = getPropValue(opt, 'attributes');
        const attrsObj = isObject(attrs) ? attrs : null;
        return String(
          getPropValue(opt, String(vKey)) ??
            getPropValue(attrsObj, String(vKey)) ??
            getPropValue(opt, 'value') ??
            getPropValue(opt, 'id') ??
            ''
        );
      },
      [vKey]
    );

    const handleChange = useCallback(
      (newValue: SingleValue<T> | MultiValue<T>, _meta: ActionMeta<T>) => {
        let selectedValue: T | T[] | null;
        if (Array.isArray(newValue)) {
          selectedValue = newValue as T[];
        } else {
          selectedValue = (newValue as T) ?? null;
        }
        setValue(selectedValue);
        // Update ref synchronously so handleBlur (fired by blurInputOnSelect before re-render)
        // sees the new value and doesn't produce a false "required" validation error.
        valueRef.current = selectedValue;

        const extractRaw = (opt: T): T['value'] =>
          (getPropValue(opt, String(vKey)) ?? getPropValue(opt, 'value')) as T['value'];

        let raw: T['value'] | T['value'][] | null;
        if (isMultiSelect) {
          const arr = Array.isArray(selectedValue) ? selectedValue : [];
          raw = arr.map(extractRaw);
        } else {
          const single = !Array.isArray(selectedValue) && selectedValue ? selectedValue : null;
          raw = single ? extractRaw(single) : null;
        }

        // Pass the full option object to onChange (matching legacy SelectControl behaviour).
        // Only call onChange when there is an actual value — callers expect the option object,
        // not null. The clear case is handled via onValueChange (null primitive) and setError.
        const hasSelection = isMultiSelect
          ? Array.isArray(selectedValue) && selectedValue.length > 0
          : selectedValue !== null;
        if (hasSelection) {
          onChangeProp?.(selectedValue);
        }
        if (name) {
          const payload: Record<string, T['value'] | T['value'][] | null> = {
            [isMultiSelect ? `${name}[]` : name]: raw,
          };
          onValueChange?.(payload);
        }
        // Trigger required validation immediately when value is cleared
        const isEmpty = isMultiSelect ? !Array.isArray(selectedValue) || selectedValue.length === 0 : !selectedValue;
        if (required && isEmpty) {
          setError?.(true);
        } else if (!isEmpty) {
          setError?.(false);
        }
      },
      [isMultiSelect, vKey, onChangeProp, onValueChange, name, required, setError]
    );

    const isValid = useCallback((): boolean => {
      if (!required) return true;
      const currentValue = valueRef.current;
      if (isMultiSelect) return Array.isArray(currentValue) && currentValue.length > 0;
      return !!currentValue;
    }, [required, isMultiSelect]);

    const checkError = useCallback(() => {
      const valid = isValid();
      if (!valid) {
        // Match legacy checkSelectError: prefer caption over name for user-facing message
        const fieldLabel = propsRef.current.caption ?? name;
        const message = fieldLabel
          ? i18n.t('%s is required', { postProcess: 'sprintf', sprintf: [fieldLabel] })
          : i18n.t('This field is required');
        setError?.(true, message);
      } else {
        setError?.(false);
      }
    }, [isValid, name, setError]);

    const handleBlur = useCallback(() => {
      const currentValue = valueRef.current;
      const extractRaw = (opt: T): T['value'] =>
        (getPropValue(opt, String(vKey)) ?? getPropValue(opt, 'value')) as T['value'];

      let raw: T['value'] | T['value'][] | null;
      if (isMultiSelect) {
        const arr = Array.isArray(currentValue) ? currentValue : [];
        raw = arr.map(extractRaw);
      } else {
        const single = !Array.isArray(currentValue) && currentValue ? currentValue : null;
        raw = single ? extractRaw(single) : null;
      }
      onBlurProp?.(raw);
      checkError();
    }, [isMultiSelect, vKey, onBlurProp, checkError]);

    useImperativeHandle(ref, () => ({ isValid, checkError }), [isValid, checkError]);

    const isClearableSelect = !(clearable === false);
    const isDisabledSelect = disabled || isDisabled || false;
    const isSearchableSelect =
      searchable !== false && isSearchable !== false && (options?.length ?? 0) > DROPDOWN_SEARCH_MINIMUM;
    const placeholderLabel = forcePlaceholder ? undefined : (placeholderText ?? placeholder ?? undefined);

    const optionsArray = Array.isArray(options) ? options : [...options];

    return (
      <div ref={containerRef}>
        <Combobox<T>
          name={isMultiSelect ? `${name}[]` : name}
          inputId={name}
          options={optionsArray}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          isMulti={isMultiSelect}
          isClearable={isClearableSelect}
          isDisabled={isDisabledSelect}
          isSearchable={isSearchableSelect}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          placeholder={placeholderLabel}
          menuPosition={menuPosition}
          aria-labelledby={`${name}-label`}
          aria-describedby={ariaDescribedBy}
        />
      </div>
    );
  }
);

// Narrowed prop types for precise onChange/onBlur/onValueChange typing
type SelectControlBaseProps<T extends OptionBase> = Omit<
  SelectControlProps<T>,
  'onChange' | 'onBlur' | 'onValueChange' | 'isMulti' | 'multi'
>;

export type SelectControlSingleProps<T extends OptionBase = SelectControlOption> = SelectControlBaseProps<T> & {
  isMulti?: false;
  multi?: false;
  onChange?: (value: T) => void;
  onValueChange?: (obj: Record<string, T['value'] | null>) => void;
  onBlur?: (value: T['value'] | null) => void;
};

export type SelectControlMultiProps<T extends OptionBase = SelectControlOption> = SelectControlBaseProps<T> & {
  isMulti?: true;
  multi?: true;
  onChange?: (value: T[]) => void;
  onValueChange?: (obj: Record<string, T['value'][] | null>) => void;
  onBlur?: (value: T['value'][] | null) => void;
};

// Overloaded component type for precise typing at call sites
type SelectControlComponentType = {
  <T extends OptionBase = SelectControlOption>(
    props: SelectControlSingleProps<T> & { ref?: React.Ref<SelectControlHandle> }
  ): React.ReactElement | null;
  <T extends OptionBase = SelectControlOption>(
    props: SelectControlMultiProps<T> & { ref?: React.Ref<SelectControlHandle> }
  ): React.ReactElement | null;
};

export const SelectControl = SelectControlInner as SelectControlComponentType;

const typedComponent = SelectControl as SelectControlComponentType & { displayName?: string };
typedComponent.displayName = 'SelectControl';
